import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Film, Loader2, Download, MessageCircle, X, Play, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * KenBurnsExport — turns a still AI-rendered image into a 5-second cinematic
 * Ken Burns clip (subtle zoom + pan) with a persistent CriLam brand overlay.
 * Uses canvas.captureStream() + MediaRecorder — no external libraries.
 *
 * Supported output formats (auto-picked by browser):
 *   1. video/mp4 (Safari 14.1+, iOS Safari, Chrome on macOS/Android)
 *   2. video/webm;codecs=vp9 or vp8 (Chrome, Firefox)
 * WhatsApp accepts both formats on modern devices.
 */
const DURATION_MS = 5000;
const FPS = 30;
const CANVAS_W = 1280;
const CANVAS_H = 720;

const pickMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return null;
  const candidates = [
    'video/mp4;codecs=avc1',
    'video/mp4',
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
  ];
  for (const m of candidates) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return null;
};

const KenBurnsExport = ({ imageUrl, brandLabel = 'CriLam AI Design Studio', filename = 'crilam-walkthrough' }) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | recording | ready | error
  const [progress, setProgress] = useState(0);
  const [videoUrl, setVideoUrl] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [ext, setExt] = useState('webm');
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const chunksRef = useRef([]);
  const recorderRef = useRef(null);

  const cleanupUrl = useCallback(() => {
    if (videoUrl) URL.revokeObjectURL(videoUrl);
  }, [videoUrl]);

  useEffect(() => () => cleanupUrl(), [cleanupUrl]);

  const drawFrame = (ctx, img, t) => {
    // t is 0..1 progress
    // Ken Burns: zoom from 1.0 → 1.15, pan gently from center toward upper-right
    const zoom = 1.0 + 0.15 * t;
    const panX = -0.05 * t * CANVAS_W;
    const panY = -0.03 * t * CANVAS_H;

    // Fit image inside canvas (cover, then zoom)
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    const scale = Math.max(CANVAS_W / iw, CANVAS_H / ih) * zoom;
    const dw = iw * scale;
    const dh = ih * scale;
    const dx = (CANVAS_W - dw) / 2 + panX;
    const dy = (CANVAS_H - dh) / 2 + panY;

    ctx.fillStyle = '#0A0D0C';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    ctx.drawImage(img, dx, dy, dw, dh);

    // Fade-in overlay in first 0.5s and fade-out in last 0.5s
    let vignette = 0;
    if (t < 0.1) vignette = (0.1 - t) * 5; // fade from black
    else if (t > 0.9) vignette = (t - 0.9) * 5; // fade to black
    if (vignette > 0) {
      ctx.fillStyle = `rgba(10,13,12,${Math.min(1, vignette)})`;
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    }

    // CriLam brand chip (bottom-right)
    const chipW = 420;
    const chipH = 46;
    const cx = CANVAS_W - chipW - 30;
    const cy = CANVAS_H - chipH - 30;
    ctx.fillStyle = 'rgba(10,13,12,0.78)';
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(cx, cy, chipW, chipH, 8);
    else ctx.rect(cx, cy, chipW, chipH);
    ctx.fill();
    ctx.fillStyle = '#D4AF37';
    ctx.fillRect(cx, cy, 4, chipH); // gold accent bar
    ctx.font = 'bold 20px Helvetica, Arial, sans-serif';
    ctx.fillStyle = '#D4AF37';
    ctx.fillText('CriLam', cx + 18, cy + 30);
    ctx.font = '15px Helvetica, Arial, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.fillText(`· ${brandLabel}`, cx + 100, cy + 30);

    // Top-left small label (nail-free)
    ctx.font = 'bold 14px Helvetica, Arial, sans-serif';
    ctx.fillStyle = '#D4AF37';
    ctx.fillText('NAIL-FREE · PUR-BONDED', 30, 40);
  };

  const startRecording = useCallback(async () => {
    setErrorMsg(null);
    setProgress(0);
    setStatus('recording');

    if (!canvasRef.current) {
      setErrorMsg('Canvas not ready. Please retry.');
      setStatus('error');
      return;
    }

    const mimeType = pickMimeType();
    if (!mimeType) {
      setErrorMsg('Video recording is not supported in this browser. Please try Chrome or Safari.');
      setStatus('error');
      return;
    }
    const detectedExt = mimeType.startsWith('video/mp4') ? 'mp4' : 'webm';
    setExt(detectedExt);

    const canvas = canvasRef.current;
    canvas.width = CANVAS_W;
    canvas.height = CANVAS_H;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#0A0D0C';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    // Load image (must be same-origin or CORS-enabled).
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise((res, rej) => {
      img.onload = res;
      img.onerror = () => rej(new Error('Could not load design image.'));
      img.src = imageUrl;
    }).catch((e) => { setErrorMsg(e.message); setStatus('error'); throw e; });

    // Capture the canvas stream
    const stream = canvas.captureStream(FPS);
    chunksRef.current = [];

    const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 5_000_000 });
    recorderRef.current = recorder;
    recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      const url = URL.createObjectURL(blob);
      setVideoUrl(url);
      setStatus('ready');
      setProgress(100);
    };
    recorder.onerror = (e) => { setErrorMsg('Recording failed. Please try again.'); setStatus('error'); console.error(e); };

    recorder.start();

    const t0 = performance.now();
    const tick = () => {
      const elapsed = performance.now() - t0;
      const t = Math.min(1, elapsed / DURATION_MS);
      drawFrame(ctx, img, t);
      setProgress(Math.round(t * 100));

      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        // Give the encoder a beat, then stop
        setTimeout(() => { try { recorder.stop(); } catch { /* ignore */ } }, 120);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [imageUrl, brandLabel]);

  const cancel = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch { /* ignore */ }
    }
  };

  const closeModal = () => {
    cancel();
    cleanupUrl();
    setVideoUrl(null);
    setStatus('idle');
    setProgress(0);
    setErrorMsg(null);
    setOpen(false);
  };

  const downloadVideo = () => {
    if (!videoUrl) return;
    const a = document.createElement('a');
    a.href = videoUrl;
    a.download = `${filename}.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success('Cinematic clip downloaded');
  };

  const shareWhatsApp = async () => {
    if (!videoUrl) return;
    try {
      const res = await fetch(videoUrl);
      const blob = await res.blob();
      const file = new File([blob], `${filename}.${ext}`, { type: blob.type });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'My CriLam Design Walkthrough',
          text: `Check out my CriLam design — Nail-Free · PUR-Bonded. Design yours: ${window.location.origin}/door-designer`,
        });
        return;
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
    }
    // Fallback: download the file, and open a WA text share the user can attach
    downloadVideo();
    const msg = `I just designed my CriLam door — Nail-Free · PUR-Bonded. The 5-second walkthrough video is now on my device — please attach it. Design yours: ${window.location.origin}/door-designer`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
    toast.info('Video saved. Attach it in the WhatsApp window that just opened.');
  };

  return (
    <>
      <button
        onClick={() => { setOpen(true); setTimeout(() => startRecording(), 200); }}
        className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-[#7A6621] to-[#D4AF37] text-[#0A0D0C] rounded-lg hover:from-[#004534] hover:to-[#004534] hover:text-white transition-colors font-bold"
        data-testid="ken-burns-btn"
      >
        <Film className="h-5 w-5" /> Create 5-sec Video
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-md flex items-center justify-center p-4"
            data-testid="ken-burns-modal"
          >
            <motion.div
              initial={{ y: 30, scale: 0.96 }} animate={{ y: 0, scale: 1 }} exit={{ y: 30, scale: 0.96 }}
              className="bg-white rounded-2xl w-full max-w-3xl overflow-hidden shadow-2xl"
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <Film className="h-5 w-5 text-[#D4AF37]" />
                  <h3 className="font-bold text-[#004534]">Cinematic Walkthrough · CriLam</h3>
                </div>
                <button onClick={closeModal} className="p-1.5 hover:bg-gray-100 rounded-full" data-testid="ken-burns-close">
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              <div className="p-6">
                {/* Preview / canvas render */}
                <div className="relative aspect-video bg-black rounded-lg overflow-hidden mb-4">
                  {status === 'ready' && videoUrl ? (
                    <video
                      src={videoUrl}
                      controls
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full"
                      data-testid="ken-burns-video"
                    />
                  ) : (
                    <canvas
                      ref={canvasRef}
                      className="w-full h-full object-contain"
                      data-testid="ken-burns-canvas"
                    />
                  )}

                  {status === 'recording' && (
                    <div className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse">
                      <div className="w-2 h-2 rounded-full bg-white" /> RECORDING · {progress}%
                    </div>
                  )}
                </div>

                {status === 'recording' && (
                  <div className="mb-3">
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-gradient-to-r from-[#004534] to-[#D4AF37]"
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        transition={{ ease: 'linear' }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2 flex items-center gap-1.5">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Composing your cinematic walkthrough with a subtle zoom & pan…
                    </p>
                  </div>
                )}

                {errorMsg && (
                  <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                    {errorMsg}
                  </div>
                )}

                {status === 'ready' && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700 text-sm">
                    <Check className="h-4 w-4 flex-shrink-0" />
                    <span>5-second cinematic clip ready! Format: {ext.toUpperCase()} · Includes CriLam brand overlay.</span>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 justify-end">
                  {status === 'ready' && (
                    <>
                      <button
                        onClick={shareWhatsApp}
                        className="flex items-center gap-2 px-4 py-2 bg-[#25D366] text-white rounded-lg hover:bg-[#128C7E] font-semibold text-sm"
                        data-testid="ken-burns-whatsapp"
                      >
                        <MessageCircle className="h-4 w-4" /> Share on WhatsApp
                      </button>
                      <button
                        onClick={downloadVideo}
                        className="flex items-center gap-2 px-4 py-2 bg-[#004534] text-white rounded-lg hover:bg-[#D4AF37] hover:text-[#004534] font-semibold text-sm"
                        data-testid="ken-burns-download"
                      >
                        <Download className="h-4 w-4" /> Download Video
                      </button>
                    </>
                  )}
                  {status === 'error' && (
                    <button
                      onClick={() => { setStatus('idle'); startRecording(); }}
                      className="flex items-center gap-2 px-4 py-2 bg-[#004534] text-white rounded-lg hover:bg-[#D4AF37] hover:text-[#004534] font-semibold text-sm"
                    >
                      <Play className="h-4 w-4" /> Retry
                    </button>
                  )}
                  {status === 'idle' && (
                    <button
                      onClick={startRecording}
                      className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#004534] rounded-lg hover:bg-[#004534] hover:text-white font-semibold text-sm"
                    >
                      <Play className="h-4 w-4" /> Start
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default KenBurnsExport;
